/**
 * Friend Inbox program — React effect runner.
 *
 * Elm-ish architecture: this hook is the runtime that connects the
 * pure update function to the side-effect handlers. The page
 * component renders from the model and dispatches messages; this
 * hook consumes effects produced by update and feeds messages back
 * into update.
 *
 * The state model is:
 *
 *   model --(dispatch msg)--> update --(effects[])--> runEffect --> dispatch
 *     ^                                                            |
 *     '----------------------------  setState  ---------------------'
 *
 * Effects produced by update are queued in a ref and drained by a
 * post-render effect. Each run captures the model's refreshToken at
 * dispatch time so stale responses can be discarded by update.
 */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import { runEffect } from './effects';
import type { FriendInboxEffect } from './effects';
import type { FriendInboxMsg } from './messages';
import type { FriendInboxModel } from './model';
import { initialUpdateResult, update } from './update';

export interface FriendInboxProgram {
  readonly model: FriendInboxModel;
  readonly dispatch: (msg: FriendInboxMsg) => void;
}

/**
 * Reducer used by useReducer. Internally calls update so the same
 * pure transition function is exercised in tests and at runtime.
 */
function reducer(model: FriendInboxModel, msg: FriendInboxMsg): FriendInboxModel {
  return update(model, msg).model;
}

export function useFriendInboxProgram(): FriendInboxProgram {
  // Initialize model by running the initial update, which kicks off
  // the LoadRequested effect on the first effect drain.
  const [model, baseDispatch] = useReducer(
    reducer,
    undefined,
    () => initialUpdateResult().model,
  );

  // Latest model is held in a ref so dispatch can produce the effects
  // for the current model synchronously without waiting for the next
  // render.
  const modelRef = useRef(model);
  modelRef.current = model;

  // Effect queue drained after each render.
  const queueRef = useRef<FriendInboxEffect[]>([]);

  const dispatch = useCallback((msg: FriendInboxMsg) => {
    const result = update(modelRef.current, msg);
    queueRef.current.push(...result.effects);
    baseDispatch(msg);
  }, []);

  useEffect(() => {
    const queued = queueRef.current;
    if (queued.length === 0) {
      return;
    }
    queueRef.current = [];
    let cancelled = false;
    for (const fx of queued) {
      if (cancelled) {
        break;
      }
      void runEffect(fx, { dispatch });
    }
    return () => {
      cancelled = true;
    };
  });

  return { model, dispatch };
}
